// Copyright (c) 2022 by Move Closer

import { computed, inject, nextTick, ref } from '@vue/composition-api'
import { Vue } from 'vue-property-decorator'

import { ComponentObjectPropsOptions, Emit } from '../../vue-api'

import {
  BreakpointsDescriptionSet,
  BreakpointSettings,
  CarouselBreakpoint,
  carouselBreakpointOptions,
  CarouselProperties,
  CarouselProps,
  CarouselResponsiveConfig,
  UseCarouselProvides
} from './Carousel.contracts'
import { defaultProvider, IS_MOBILE_PROVIDER_KEY } from '../../../support'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const carouselProps: ComponentObjectPropsOptions<CarouselProps> = {
  /**
   * @see CarouselProps.autoplay
   */
  autoplay: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * @see CarouselProps.autoHeight
   */
  autoHeight: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see CarouselProps.centralize
   */
  centralize: {
    type: Boolean,
    required: false,
    default: false
  },

  draggable: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * @see CarouselProps.delay
   */
  delay: {
    type: Number,
    required: false,
    default: 2000
  },

  /**
   * @see CarouselProps.hasButtons
   */
  hasButtons: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * @see CarouselProps.hasPagination
   */
  hasPagination: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * @see CarouselProps.initialSlide
   */
  initialSlide: {
    type: Number,
    required: false,
    default: 0
  },

  /**
   * @see CarouselProps.inLoop
   */
  inLoop: {
    type: Boolean,
    required: false,
    default: true
  },

  /**
   * @see CarouselProps.pauseOnHover
   */
  pauseOnHover: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see CarouselProps.perPage
   */
  perPage: {
    type: Number,
    required: false,
    default: 1
  },

  perScroll: {
    type: Number,
    required: false,
    default: 1
  },

  /**
   * @see CarouselProps.responsive
   */
  responsive: {
    type: Object,
    required: false,
    default: null
    // default: () => ({
    //   mobile: { perPage: 1 },
    //   desktop: { perPage: 4 }
    // })
  },

  swipeToSlide: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see CarouselProps.vertical
   */
  vertical: {
    type: Boolean,
    required: false,
    default: false
  },

  /**
   * @see CarouselProps.variableWidth
   */
  variableWidth: {
    type: Boolean,
    required: false,
    default: false
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useCarousel = (props: CarouselProps, emit: Emit): UseCarouselProvides => {
  const dragging = ref<boolean>(false)
  const carouselRef = ref<Vue | null>(null)
  const slidesPerPage = ref<number | null>(null)

  const isMobile = inject(IS_MOBILE_PROVIDER_KEY, () => defaultProvider<boolean>(false))

  /**
   * @inheritDoc
   */
  function onInit (): void {
    nextTick(() => {
      emit('onInit', carouselRef.value)
    })
  }

  function onReInit (): void {
    emit('onReInit', carouselRef.value)
  }

  function getCurrentSlideIndex (): number | undefined {
    if (carouselRef && carouselRef.value) {
      const cElement = carouselRef.value.$el.querySelector('.slick-current')

      let cIndex

      if (cElement) {
        cIndex = Number(cElement.getAttribute('data-index'))
      }

      return cIndex
    }
  }

  /**
   * @inheritDoc
   */
  function onClick (): void {
    const currentSlideIndex = getCurrentSlideIndex()
    emit('onSlideClick', currentSlideIndex)
  }

  /**
   * @inheritDoc
   */
  function onSwipe (): void {
    const currentSlideIndex = getCurrentSlideIndex()
    emit('onSlideChange', currentSlideIndex)
    dragging.value = true
  }

  /**
   * @inheritDoc
   */
  function handleOnItemClick (e: Event): void {
    if (dragging.value) {
      e.stopPropagation()
      e.preventDefault()
      dragging.value = false
    }
  }

  /**
   * Build slides per view for different breakpoints.
   */
  const carouselProps = computed<CarouselProperties>(() => {
    const mobileSettings = parseResonsiveProps('mobile')
    const desktopSettings = parseResonsiveProps('desktop')
    const defaultProps = (isMobile() ? mobileSettings : desktopSettings) ?? {}

    return {
      autoHeight: props.autoHeight,
      slidesToScroll: props.perScroll ?? props.perPage,
      slidesToShow: props.perPage,
      swipeToSlide: props.swipeToSlide,
      dots: props.hasPagination,
      arrows: props.hasButtons,
      variableWidth: props.variableWidth,
      ...defaultProps,
      responsive: buildBreakpoints(mobileSettings, desktopSettings)
    }
  })

  const buildBreakpoints = (mobileSettings: BreakpointSettings | null, desktopSettings: BreakpointSettings | null): BreakpointsDescriptionSet => {
    return [
      ...(mobileSettings ? [
        {
          breakpoint: carouselBreakpointOptions[CarouselBreakpoint.Mobile],
          settings: mobileSettings
        }
      ] : []),
      ...(desktopSettings ? [
        {
          breakpoint: carouselBreakpointOptions[CarouselBreakpoint.XLargeDesktop],
          settings: desktopSettings
        }
      ] : [])
    ]
  }

  const parseResonsiveProps = (breakpoint: keyof CarouselResponsiveConfig): BreakpointSettings | null => {
    if (!props.responsive) {
      return null
    }

    const { perPage, ...options } = props.responsive[breakpoint]
    const slidesPerPage = Math.floor(Number(perPage))
    return {
      ...('autoplay' in options ? { autoplay: options.autoplay } : {}),
      ...('hasButtons' in options ? { arrows: options.hasButtons } : {}),
      ...('hasPagination' in options ? { dots: options.hasPagination } : {}),
      ...('variableWidth' in options ? { variableWidth: options.variableWidth } : {}),
      ...('useTransform' in options ? { useTransform: options.useTransform } : {}),
      ...('inLoop' in options ? { infinite: options.inLoop } : {}),
      ...options,
      slidesToScroll: slidesPerPage,
      slidesToShow: options.disableFlooredPerPage ? perPage : slidesPerPage
    }
  }

  return {
    carouselProps,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    carouselRef,
    onInit,
    onReInit,
    onClick,
    onSwipe,
    slidesPerPage,
    handleOnItemClick
  }
}
